import accounting from "accounting";
import { CURRENCY_MAPPING } from "../Constants";
import { Money } from "../Types";
import { escapeRegExp } from "./TextHelper";

/** Builds a regex that matches the given currency symbol at the start of a string, followed by any whitespace, e.g. for `"$ 1,234"` → strips the leading `"$ "`. */
const leadingCurrencySymbolWithSpace = (escapedSymbol: string) => new RegExp(`^${escapedSymbol}\\s*`);

/**
 * Formats a numeric amount with x decimal places together with a given currency,
 * eg. (1020, 2, USD) => $ 1,020.00.
 * @param {number} amount
 * @param {number} precision
 * @param currency
 * @param showSignForPositiveValues forces showing + sign for positive values
 * @returns {string}
 */
export const FormatCurrency = (
  amount: number,
  currency: string,
  precision: number = 2,
  showSignForPositiveValues: boolean = false
) => {
  const prefix = showSignForPositiveValues && amount > 0 ? "+" : "";
  const currencyCode = currency?.toUpperCase();

  const currencyInfo = CURRENCY_MAPPING.find((c) => c.currencyCode == currencyCode);
  const currencySymbol = currencyInfo?.currencySymbol;
  const effectivePrecision = currencyInfo?.noFractionalUnits ? 0 : precision;
  const isAfter = currencyInfo?.displayPosition == "after";
  const currencyFormatting = isAfter ? `${prefix}%v %s` : `%s ${prefix}%v`;

  const isNegative = amount < 0;
  const valueForFormat =
    showSignForPositiveValues && amount > 0 ? `+${amount}` : isNegative ? Math.abs(amount) : amount;

  const out = accounting.formatMoney(valueForFormat, currencySymbol, effectivePrecision, ",", ".", currencyFormatting);

  if (isNegative) {
    if (isAfter || !currencySymbol) {
      return `-${out}`;
    }
    const escapedSymbol = escapeRegExp(currencySymbol);
    const afterSymbol = out.trim().replace(leadingCurrencySymbolWithSpace(escapedSymbol), "");
    return `-${currencySymbol}${afterSymbol}`;
  }

  return out;
};

export const FormattedMoney = (money: Money, precision: number = 2, showPlusForPositiveValues = false) => {
  return FormatCurrency(money.amount, money.currency, precision, showPlusForPositiveValues);
};
